/**
 * Sign in.
 *
 * Keycloak authenticates every person. A visitor who has no session goes
 * straight to Keycloak, so this page is not a step that a person reads. It
 * appears only when the sign-in cannot start, or when it ended with a reason
 * that the person must see.
 *
 * ArcGRC never receives a password, so the page holds no password field.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { Note, Problem, buttonClass } from "../components/primitives.tsx";
import { api } from "../lib/api.ts";

interface SignInMethods {
  readonly keycloak: boolean;
  readonly note: string;
}

/**
 * The reasons that the server sends back on the address.
 *
 * The sign-in must not start again for any of these, because the answer would
 * be the same and the person would never read the reason.
 */
const REASONS: Record<string, string> = {
  refused:
    "The identity provider refused the sign-in. Try again, or ask the " +
    "operator for help.",
  inactive:
    "This account is not active. Ask the operator for a new account.",
};

// One automatic start for each browser tab. A second start within this time
// means the sign-in returned without a session, and another start would loop.
const GUARD_KEY = "arcgrc.sign-in.started";
const GUARD_MS = 20_000;

function startedRecently(): boolean {
  try {
    const raw = window.sessionStorage.getItem(GUARD_KEY);
    if (raw === null) {
      return false;
    }
    return Date.now() - Number(raw) < GUARD_MS;
  } catch {
    // A browser that refuses storage still signs in. It loses the guard only.
    return false;
  }
}

function recordStart(): void {
  try {
    window.sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {
    // The guard is a convenience. Its absence must not stop the sign-in.
  }
}

export function SignInPage() {
  const methods = useQuery({
    queryKey: ["sign-in-methods"],
    queryFn: () => api.get<SignInMethods>("/api/auth/methods"),
    retry: false,
  });

  const parameters = new URLSearchParams(window.location.search);
  const reason = REASONS[parameters.get("signin") ?? ""];
  // The sign-in leaves this application, so the browser follows the address
  // rather than reading an answer.
  const returnPath = `${window.location.pathname}${window.location.search}`;
  const signInUrl = `/api/auth/sign-in?returnPath=${encodeURIComponent(returnPath)}`;

  const keycloakReady = methods.data?.keycloak === true;
  const automatic = keycloakReady && reason === undefined && !startedRecently();

  useEffect(() => {
    if (!automatic) {
      return;
    }
    recordStart();
    // replace, so the browser's back button returns to the page before this
    // one and not into the sign-in again.
    window.location.replace(signInUrl);
  }, [automatic, signInUrl]);

  if (automatic) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 px-4">
        <img src="/brand/arcgrc-logo.svg" alt="ArcGRC" className="h-9 w-auto self-start" />
        <p className="text-sm text-muted">Opening the sign-in.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4">
      <img src="/brand/arcgrc-logo.svg" alt="ArcGRC" className="h-9 w-auto self-start" />
      <h1 className="text-2xl font-semibold">Sign in</h1>

      {reason !== undefined && <Problem message={reason} />}

      <Note>
        Sign-in, multifactor authentication and account recovery use Keycloak.
      </Note>

      {keycloakReady && (
        <a
          className={buttonClass("main")}
          href={signInUrl}
        >
          Sign in with your organization account
        </a>
      )}

      {methods.data !== undefined && !methods.data.keycloak && (
        <Problem message={methods.data.note} />
      )}

      <p className="text-sm text-muted">
        Public account requests are off. Authorized staff create each
        organization and invite its people.
      </p>
    </div>
  );
}
